import { formatTimeForDisplay } from './timeUtils';

/**
 * Format a schedule description into a human-readable string
 * @param {Object} schedule - The schedule object
 * @param {string} schedule.scheduleType - Schedule type (HOURLY, DAILY, WEEKLY, MONTHLY)
 * @param {number} schedule.scheduleInterval - Interval (used for HOURLY schedules)
 * @param {string} schedule.scheduleTime - Time in HH:MM format (used for DAILY, WEEKLY, MONTHLY)
 * @param {Array<string>} schedule.scheduleDays - Days of week for WEEKLY schedules 
 * @param {number} schedule.scheduleDay - Day of month for MONTHLY schedules
 * @param {boolean} isUTC - Whether the scheduleTime is in UTC and needs to be converted
 * @returns {string} Formatted schedule description
 */
export const formatSchedule = (schedule, isUTC = false) => {
  console.group('🔍 formatSchedule DIAGNOSTICS');
  
  // Enhanced debug logging
  console.log('formatSchedule received raw input:', schedule);
  console.log('scheduleTime value:', schedule?.scheduleTime);
  console.log('scheduleTime type:', typeof schedule?.scheduleTime);
  console.log('isUTC flag:', isUTC);
  
  // Guard against undefined or invalid input
  if (!schedule || !schedule.scheduleType) {
    console.log('Invalid schedule data, returning default');
    console.groupEnd();
    return 'Custom schedule';
  }
  
  const { scheduleType, scheduleInterval, scheduleTime, scheduleDays, scheduleDay } = schedule;
  
  // Extract time string from TimeValue object if needed
  let timeToFormat = '';
  
  // Debug - show exactly what the scheduleTime object contains
  if (scheduleTime && typeof scheduleTime === 'object') {
    console.log('scheduleTime as object:', JSON.stringify(scheduleTime, null, 2));
    // Also show all properties on the object
    console.log('scheduleTime properties:', Object.getOwnPropertyNames(scheduleTime));
    
    // Check for value property (TimeValue-like)
    if (scheduleTime.value !== undefined && typeof scheduleTime.value === 'string') {
      console.log('Using .value property from TimeValue-like object:', scheduleTime.value);
      timeToFormat = scheduleTime.value;
    } 
    // Check if it's a TimeValue instance by checking for the expected properties and methods
    else if (scheduleTime.value !== undefined && scheduleTime.format !== undefined) {
      console.log('Detected TimeValue object with value and format properties');
      timeToFormat = scheduleTime.value || '';
      console.log('Using TimeValue.value:', timeToFormat);
    }
    // Fallback to constructor name check instead of instanceof
    else if (scheduleTime.constructor && scheduleTime.constructor.name === 'TimeValue') {
      console.log('Object has TimeValue constructor but no value property');
      // Try to access the value via toString
      try {
        timeToFormat = scheduleTime.toString();
        console.log('Using toString() result:', timeToFormat);
      } catch (err) {
        console.error('Error using toString():', err);
      }
    }
    // General object with toString method
    else if (scheduleTime.toString && typeof scheduleTime.toString === 'function') {
      try {
        const stringValue = scheduleTime.toString();
        // Only use toString result if it looks like a time string
        if (stringValue.includes(':')) {
          console.log('Using toString() method to get time string:', stringValue);
          timeToFormat = stringValue;
        } else {
          console.log('toString() did not return a time-like string:', stringValue);
        }
      } catch (err) {
        console.error('Error calling toString():', err);
      }
    }
  } else if (typeof scheduleTime === 'string') {
    // Plain string - use directly
    timeToFormat = scheduleTime;
    console.log('Using scheduleTime as plain string:', timeToFormat);
  }
  
  // Provide a default if we couldn't extract a valid time
  if (!timeToFormat) {
    console.log('Could not extract a valid time string, using default');
    timeToFormat = '09:00:00';
  }
  
  // Use the utility function to format the time consistently
  // If isUTC is true, we'll first convert from UTC to local time
  console.log('Calling formatTimeForDisplay with timeToFormat:', timeToFormat);
  const formattedTime = formatTimeForDisplay(timeToFormat, isUTC);
  console.log('Final formatted time:', formattedTime);
  
  let result;
  if (scheduleType === 'HOURLY') {
    result = `Every ${scheduleInterval || 1} hour${scheduleInterval !== 1 ? 's' : ''}`;
  } else if (scheduleType === 'DAILY') {
    result = `Daily at ${formattedTime}`;
  } else if (scheduleType === 'WEEKLY') {
    // Format days of week
    const days = scheduleDays && Array.isArray(scheduleDays) && scheduleDays.length > 0 
      ? scheduleDays.map(day => formatWeekday(day)).join(', ') 
      : 'selected days';
    result = `Weekly on ${days} at ${formattedTime}`;
  } else if (scheduleType === 'MONTHLY') {
    result = `Monthly on day ${scheduleDay || 1} at ${formattedTime}`;
  } else {
    result = 'Custom schedule';
  }
  
  console.log('Final schedule string:', result);
  console.groupEnd();
  return result;
};

/**
 * Format a weekday constant into a readable format
 * @param {string} day - Day constant (e.g., "MONDAY")
 * @returns {string} Formatted day name
 */
const formatWeekday = (day) => {
  if (!day) return '';
  
  // Convert from uppercase constant to title case
  const dayMap = {
    'MONDAY': 'Monday',
    'TUESDAY': 'Tuesday',
    'WEDNESDAY': 'Wednesday',
    'THURSDAY': 'Thursday',
    'FRIDAY': 'Friday',
    'SATURDAY': 'Saturday',
    'SUNDAY': 'Sunday'
  };
  
  return dayMap[day] || day;
};

// Add a test function to generate sample schedule data for all types
export const generateTestSchedules = () => {
  return [
    // HOURLY Schedule
    {
      title: "Test Hourly Schedule",
      scheduleType: "HOURLY",
      scheduleInterval: 2,
      scheduleTime: null,
      scheduleDays: null,
      scheduleDay: null
    },
    // DAILY Schedule
    {
      title: "Test Daily Schedule",
      scheduleType: "DAILY", 
      scheduleInterval: 1,
      scheduleTime: "14:30",  // 2:30 PM
      scheduleDays: null,
      scheduleDay: null
    },
    // WEEKLY Schedule
    {
      title: "Test Weekly Schedule",
      scheduleType: "WEEKLY",
      scheduleInterval: 1,
      scheduleTime: "10:15",  // 10:15 AM
      scheduleDays: ["MONDAY", "WEDNESDAY", "FRIDAY"],
      scheduleDay: null
    },
    // MONTHLY Schedule
    {
      title: "Test Monthly Schedule",
      scheduleType: "MONTHLY",
      scheduleInterval: 1,
      scheduleTime: "08:00",  // 8:00 AM
      scheduleDays: null,
      scheduleDay: 15  // 15th day of month
    }
  ];
};

export default formatSchedule; 